import type React from "react";
import { useState } from "react";
import { useLoaderData } from "react-router-dom";
import { PerformanceCard } from "../components/PerformanceCard";
import { byStartTime } from "../lib/sorting";
import {
  getAllPerformances,
  getArtistById,
  getDayById,
  getStageById,
  loadFestivalData,
} from "../services/dataService";
import type { Artist, Day, Performance, Stage } from "../types";

export type EnrichedPerformance = Performance & {
  artist: Artist | null;
  stage: Stage | null;
  day: Day | null;
};

export const loadSearchPageData = async (): Promise<EnrichedPerformance[]> => {
  await loadFestivalData();
  const performances = getAllPerformances();

  const enrichedPerformances = performances
    .map((performance) => ({
      ...performance,
      artist: getArtistById(performance.artistId) ?? null,
      stage: getStageById(performance.stageId) ?? null,
      day: getDayById(performance.dayId) ?? null,
    }))
    .sort(byStartTime);

  return enrichedPerformances;
};

const SearchTarget = {
  ARTISTS: "artists",
  ALL: "all",
} as const;

type SearchTargetType = (typeof SearchTarget)[keyof typeof SearchTarget];

export const SearchPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTarget, setSearchTarget] = useState<SearchTargetType>(SearchTarget.ALL);
  const programmeData = useLoaderData() as EnrichedPerformance[];
  const [searchResults, setSearchResults] = useState<EnrichedPerformance[]>(programmeData);

  const handleSearch = (termVal: string, targetVal: SearchTargetType = searchTarget) => {
    const term = termVal.toLowerCase();
    setSearchTerm(termVal);

    if (!term) {
      setSearchResults(programmeData);
      return;
    }

    const results = programmeData.filter((item) => {
      const artistName = item.artist?.name?.toLowerCase() || "";
      const description = item.description?.toLowerCase() || "";

      switch (targetVal) {
        case SearchTarget.ARTISTS:
          return artistName.includes(term);
        case SearchTarget.ALL:
          return (
            artistName.includes(term) ||
            description.includes(term) ||
            (item.artist?.genre?.toLowerCase() || "").includes(term) ||
            (item.artist?.collective?.toLowerCase() || "").includes(term)
          );
        default:
          return false;
      }
    });

    setSearchResults(results);
  };

  const handleTargetChange = (target: SearchTargetType) => {
    setSearchTarget(target);
    handleSearch(searchTerm, target);
  };

  const handleClear = () => {
    setSearchTerm("");
    setSearchResults(programmeData);
  };

  return (
    <div className="space-y-6 pb-6">
      {/* Search Bar Container */}
      <div className="max-w-md mx-auto bg-[#222222]/65 border border-white/5 p-5 rounded-2xl backdrop-blur-md space-y-4">
        {/* Toggle Options */}
        <div className="flex gap-2">
          <button
            onClick={() => handleTargetChange(SearchTarget.ARTISTS)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 border ${
              searchTarget === SearchTarget.ARTISTS
                ? "bg-main border-transparent text-[#222222] shadow-[0_0_12px_rgba(34,211,186,0.4)]"
                : "bg-white/5 border-white/10 text-muted hover:text-white"
            }`}
            type="button"
          >
            Előadó Neve
          </button>

          <button
            onClick={() => handleTargetChange(SearchTarget.ALL)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 border ${
              searchTarget === SearchTarget.ALL
                ? "bg-main border-transparent text-[#222222] shadow-[0_0_12px_rgba(34,211,186,0.4)]"
                : "bg-white/5 border-white/10 text-muted hover:text-white"
            }`}
            type="button"
          >
            Összes Mező
          </button>
        </div>

        {/* Input Field */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Keresés előadókra vagy műsorokra..."
            className="w-full px-4 py-3 pl-10 pr-10 text-sm border border-white/5 rounded-xl bg-background/80 text-white placeholder-muted/60 outline-none focus:border-main focus:shadow-[0_0_10px_rgba(34,211,186,0.15)] transition-all"
          />
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted/60 pointer-events-none">
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={handleClear}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#9f98b5] hover:text-white text-sm font-semibold transition-colors"
              type="button"
              aria-label="Keresőmező törlése"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Results Rendering */}
      {searchResults.length > 0 ? (
        <div className="space-y-3">
          {searchResults.map((item) => (
            <PerformanceCard
              key={item.id}
              performance={{
                ...item,
                artist: item.artist || undefined,
                stage: item.stage || undefined,
                day: item.day || undefined,
              }}
            />
          ))}
        </div>
      ) : searchTerm ? (
        <div className="text-center py-12 bg-card border border-border rounded-2xl">
          <p className="text-[#9f98b5] text-sm">Nincs találat a keresésre.</p>
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-2xl">
          <p className="text-[#9f98b5] text-sm">Írjon be egy keresési kulcsszót...</p>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
